import type { JSX, Ref } from 'react';
import { useMemo, useState } from 'react';

import { EloIcon, type EloIconType } from '@plurall/elo-v3';

import { AppNavIconButtonFactory } from './AppNavIconButton.factory';

type AppNavIconButtonProps = {
  ref?: Ref<HTMLButtonElement>;
  'aria-label': string;
  className?: string;
  icon: EloIconType;
  iconHover?: EloIconType;
  isActive?: boolean;
  onClick?: (event: unknown) => void;
  onFocus?: (event: unknown) => void;
  onBlur?: (event: unknown) => void;
  disabled?: boolean;
  [key: string]: unknown;
};

export function AppNavIconButton({
  ref,
  'aria-label': ariaLabel,
  className,
  icon,
  iconHover,
  isActive = false,
  onClick,
  onFocus,
  onBlur,
  disabled,
  ...rest
}: AppNavIconButtonProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const resolvedIcon = useMemo(() => {
    if (!iconHover) return icon;
    return isHovered || isFocused || isActive ? iconHover : icon;
  }, [icon, iconHover, isActive, isHovered, isFocused]);

  return (
    <AppNavIconButtonFactory
      {...rest}
      ref={ref}
      type='button'
      aria-label={ariaLabel}
      className={className}
      isActive={isActive}
      disabled={disabled}
      onClick={(e) => {
        onClick?.(e);
      }}
      onFocus={(e) => {
        onFocus?.(e);
        const el = e.currentTarget as HTMLElement;
        setIsFocused(el?.matches?.(':focus-visible') ?? false);
      }}
      onBlur={(e) => {
        onBlur?.(e);
        setIsFocused(false);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <EloIcon
        icon={resolvedIcon}
        size='md'
      />
    </AppNavIconButtonFactory>
  );
}
